/**
 * Analytics Service
 *
 * Provider-agnostic analytics abstraction. Currently uses PostHog,
 * but can be swapped to any analytics provider (Mixpanel, Amplitude, etc.)
 * by updating this file only.
 */

import * as posthog from './posthog'

// Re-export event constants
export { ANALYTICS_EVENTS } from './posthog'

// Types
export interface UserProperties {
  email?: string
  firstName?: string
  lastName?: string
  subscriptionTier?: string
  subscriptionStatus?: string
  brandCount?: number
  [key: string]: unknown
}

export interface AnalyticsProvider {
  init: () => void
  identify: (userId: string, properties?: UserProperties) => void
  reset: () => void
  track: (event: string, properties?: Record<string, unknown>) => void
  page: (path?: string) => void
  setUserProperties: (properties: Record<string, unknown>) => void
  isEnabled: () => boolean
}

// Current provider implementation (PostHog)
const currentProvider: AnalyticsProvider = {
  init: posthog.initPostHog,
  identify: posthog.identifyUser,
  reset: posthog.resetUser,
  track: posthog.trackEvent,
  page: posthog.trackPageView,
  setUserProperties: posthog.setUserProperties,
  isEnabled: posthog.isPostHogEnabled
}

/**
 * Initialize the analytics provider
 * Call this once on app load
 */
export function initAnalytics(): void {
  currentProvider.init()
}

/**
 * Identify a user after login
 * @param userId - Unique user identifier
 * @param properties - Optional user properties to set
 */
export function identifyUser(userId: string, properties?: UserProperties): void {
  currentProvider.identify(userId, properties)
}

/**
 * Reset user identity on logout
 * Clears all user data from the analytics provider
 */
export function resetUser(): void {
  currentProvider.reset()
}

/**
 * Track a custom event
 * @param event - Event name (use ANALYTICS_EVENTS constants)
 * @param properties - Optional event properties
 */
export function trackEvent(event: string, properties?: Record<string, unknown>): void {
  currentProvider.track(event, properties)
}

/**
 * Track a page view
 * @param path - Optional path override (defaults to current URL)
 */
export function trackPageView(path?: string): void {
  currentProvider.page(path)
}

/**
 * Set user properties without identifying
 * @param properties - Properties to set on the current user
 */
export function setUserProperties(properties: Record<string, unknown>): void {
  currentProvider.setUserProperties(properties)
}

/**
 * Check if analytics is enabled
 * @returns true if analytics provider is configured and active
 */
export function isAnalyticsEnabled(): boolean {
  return currentProvider.isEnabled()
}

// Default export for convenience
export default {
  init: initAnalytics,
  identify: identifyUser,
  reset: resetUser,
  track: trackEvent,
  page: trackPageView,
  setUserProperties,
  isEnabled: isAnalyticsEnabled
}
